/**
 * LinkLeadModal
 * =============
 * Shared modal for linking an orphan (unlinked) meeting to an existing lead.
 * Used by both the meetings list and the meeting detail page so the search +
 * link behaviour lives in one place.
 *
 * Props:
 *   isOpen    — whether the modal is shown
 *   onClose   — called to dismiss the modal
 *   meeting   — the meeting being linked ({ id, title })
 *   onLinked  — called with the selected lead after a successful link; the
 *               parent decides what to do next (refetch list, navigate, etc.)
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../ui';
import { leadAPI } from '../../api';
import meetingAPI from '../../api/meetings';
import { useToast } from '../../hooks';

const LinkLeadModal = ({ isOpen, onClose, meeting, onLinked }) => {
  const { showToast } = useToast();
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [linking, setLinking] = useState(false);

  // Reset state whenever the modal opens (for a possibly different meeting).
  useEffect(() => {
    if (isOpen) {
      setLeadSearch('');
      setLeadResults([]);
      setSelectedLead(null);
      setLinking(false);
    }
  }, [isOpen, meeting?.id]);

  // Debounced lead search.
  useEffect(() => {
    if (!leadSearch || leadSearch.length < 2) {
      setLeadResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await leadAPI.listLeads({ search: leadSearch, limit: 10 });
        if (response.data && response.data.leads) {
          setLeadResults(response.data.leads);
        }
      } catch (err) {
        console.error('Error searching leads:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [leadSearch]);

  const handleLink = async () => {
    if (!selectedLead || !meeting) return;
    setLinking(true);
    try {
      await meetingAPI.linkMeetingToLead(meeting.id, {
        lead_id: selectedLead.id,
        trigger_questionnaire: true,
      });
      showToast(`Meeting linked to ${selectedLead.name}`, 'success');
      onLinked?.(selectedLead);
      onClose?.();
    } catch (err) {
      console.error('Error linking meeting:', err);
      showToast('Failed to link meeting to lead', 'error');
    } finally {
      setLinking(false);
    }
  };

  // Block every dismiss path (Escape, overlay, X) while a link is in flight —
  // Modal calls onClose on Escape regardless of closeOnOverlayClick.
  const handleClose = () => {
    if (linking) return;
    onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Link to Lead"
      size="md"
      closeOnOverlayClick={!linking}
      showCloseButton={!linking}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={linking}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleLink} disabled={!selectedLead || linking}>
            {linking ? 'Linking...' : 'Link to Lead'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Link "{meeting?.title || 'this meeting'}" to an existing lead
      </p>

      {/* Search Input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search leads by name or company..."
          value={leadSearch}
          onChange={(e) => setLeadSearch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
          autoFocus
        />
      </div>

      {/* Lead Results */}
      <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-[#3A3C3E] rounded-lg">
        {searching ? (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">Searching...</div>
        ) : leadResults.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {leadSearch.length < 2 ? 'Type at least 2 characters to search' : 'No leads found'}
          </div>
        ) : (
          leadResults.map((lead) => (
            <button
              key={lead.id}
              onClick={() => setSelectedLead(lead)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-[#3A3C3E] last:border-b-0 hover:bg-gray-50 dark:hover:bg-[#2A2C2E] transition-colors ${
                selectedLead?.id === lead.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
              }`}
            >
              <div className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{lead.company}</div>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
};

export default LinkLeadModal;
